import React from "react";
import ResourcePricingCard from "../../components/ResourcePricingCard";
import useAzurePricing from "./useAzurePricing";

const normalize = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const text = (value) => typeof value === "string" || typeof value === "number"
  ? String(value).trim() : "";

const getSelection = (formData, formConfig, dropdownData, names) => {
  const field = formConfig.find((candidate) => names.includes(normalize(candidate.fieldName))) ??
    formConfig.find((candidate) => names.includes(normalize(candidate.fieldLabel)));
  const key = field?.fieldName ??
    Object.keys(formData).find((name) => names.includes(normalize(name)));
  const selection = formData[key];
  const value = selection?.value ?? selection;
  const option = dropdownData[key]?.find((item) => text(item.value) === text(value));
  return { ...option, value, label: selection?.label || option?.label };
};

const AzureVirtualMachinePricing = ({
  formData,
  formConfig = [],
  dropdownData = {},
  organizationName,
  onPriceChange,
  onStatusChange,
}) => {
  const read = (names) => getSelection(formData, formConfig, dropdownData, names);
  const location = read(["location", "region"]);
  const size = read(["instancetype", "vmsize", "size"]);
  const image = read(["image", "imageid", "osimage", "selectedimage"]);
  const platform = read(["platformdetails", "ostype", "operatingsystem", "selectedimageos"]);
  const diskSize = read(["osdisksizegb", "osdisksize"]);
  const diskType = read(["osdisktype"]);
  const redundancy = read(["osdiskredundancy", "redundancy"]);
  const combinedDiskType = [text(diskType.value), text(diskType.label)]
    .map((value) => value.match(/^(Standard SSD|Premium SSD)\s+(LRS|ZRS)$/i))
    .find(Boolean);
  const osDiskType = combinedDiskType
    ? `${combinedDiskType[1].toLowerCase().startsWith("premium") ? "Premium" : "Standard"} SSD`
    : text(diskType.value);
  const osDiskRedundancy = text(redundancy.value) || combinedDiskType?.[2].toUpperCase() || "";
  const imageId = text(image.value);
  const instanceType = text(size.vmSizeName) ||
    (text(size.value).match(/^Standard_[^\s(]+$/i)?.[0]) ||
    text(size.label).match(/^Standard_[^\s(]+/i)?.[0] || text(size.value);
  const os = text(image.osType || platform.value || image.label);
  const platformDetails = /windows/i.test(os) ? "Windows" :
    /linux|ubuntu|debian|rhel|red hat|centos|suse|rocky|alma/i.test(os) ? "Linux" : undefined;
  const osDiskSizeGb = Number(diskSize.value);
  const params = {
    resourceType: "Virtual Machine",
    location: text(location.value),
    instanceType,
    platformDetails,
    osDiskSizeGb,
    osDiskType,
    osDiskRedundancy,
    image: imageId,
    // The current backend resolves the selected image OS using ImageId.
    imageId,
  };
  const enabled = Boolean(
    params.location && params.instanceType && /^[1-9]\d*$/.test(imageId) &&
    Number.isSafeInteger(osDiskSizeGb) && osDiskSizeGb > 0 &&
    params.osDiskType && params.osDiskRedundancy
  );
  const price = useAzurePricing({
    params,
    enabled,
    resourceName: "Virtual Machine",
    onPriceChange,
    onStatusChange,
  });

  if (!price) return null;
  return <ResourcePricingCard price={price} organizationName={organizationName} />;
};

export default AzureVirtualMachinePricing;
